// eslint-disable-next-line no-restricted-imports
import { State } from 'app/gamma/src/modules/types';

import { ProductFeatures } from '@trello/product-features';
// eslint-disable-next-line no-restricted-imports
import {
  AccessLevel,
  PermissionLevel,
  MemberModel,
  MembershipModel,
  TeamModel,
} from 'app/gamma/src/types/models';
import { getMe, getMemberById } from './members';
import { getMemberTypeFromMemberships } from './memberships';
import { memberId } from '@trello/session-cookie';
import { isPaidManagedEnterpriseMember } from '@trello/members';

export function getMyTeams(state: State) {
  if (!memberId) {
    return [];
  }

  const inIdOrganizations = new Map<string, boolean>();
  const me = getMe(state);
  if (me && me.idOrganizations) {
    me.idOrganizations.forEach((id) => inIdOrganizations.set(id, true));
  }

  return state.models.teams.filter(
    ({ id, memberships }: { id: string; memberships?: MembershipModel[] }) => {
      return (
        inIdOrganizations.get(id) &&
        (!memberships ||
          memberships.some(({ idMember }) => idMember === memberId))
      );
    },
  );
}

const filterTeams = (
  teams: TeamModel[],
  filterFn: (team: TeamModel) => boolean,
) => {
  return teams.filter(filterFn)[0] || null;
};

const filterMyTeams = (
  state: State,
  filterFn: (team: TeamModel) => boolean,
) => {
  const teams = getMyTeams(state);

  if (teams.length === 0) {
    return null;
  }

  return filterTeams(teams, filterFn);
};

export function getTeamById(state: State, idOrganization: string) {
  return filterTeams(state.models.teams, ({ id }) => id === idOrganization);
}

export const getMyTeamById = (state: State, idOrganization: string) =>
  filterMyTeams(state, ({ id }) => id === idOrganization);

export function getTeamMembershipType(
  state: State,
  org: TeamModel | string,
  member: MemberModel | string,
): AccessLevel {
  const teamModel: TeamModel =
    typeof org === 'string' ? getTeamById(state, org) : org;
  const memberModel =
    typeof member === 'string' ? getMemberById(state, member)! : member;

  return getMemberTypeFromMemberships(
    state,
    teamModel.memberships,
    memberModel,
    teamModel.id,
  );
}

export function isOrganizationOwned(state: State, org: TeamModel | string) {
  return getTeamMembershipType(state, org, getMe(state) || '') === 'admin';
}

export function isMemberOfTeam(
  state: State,
  org: TeamModel | string,
  member: MemberModel | string,
): boolean {
  const teamModel: TeamModel =
    typeof org === 'string' ? getTeamById(state, org) : org;

  if (!teamModel) {
    return false;
  }

  const memberType = getTeamMembershipType(state, org, member);

  return !!memberType && ['virtual', 'normal', 'admin'].includes(memberType);
}

export function teamCanSetVisibility(
  state: State,
  org: TeamModel | string,
  vis: PermissionLevel,
) {
  const teamModel: TeamModel =
    typeof org === 'string' ? getTeamById(state, org) : org;

  if (!teamModel) {
    return false;
  }

  const isRestrictVisEnabled = teamModel.premiumFeatures?.includes(
    'restrictVis',
  );

  if (!isRestrictVisEnabled) {
    return true;
  }

  if (vis === 'enterprise' && !teamModel.idEnterprise) {
    return false;
  }

  const pref = teamModel.prefs?.boardVisibilityRestrict?.[vis];

  return (
    !pref ||
    pref === 'org' ||
    (pref === 'admin' && isOrganizationOwned(state, org))
  );
}

export interface OrgWithPermissions {
  allowedVis: PermissionLevel[];
  model: TeamModel;
}

export function getOrgsWithPermissions(state: State): OrgWithPermissions[] {
  const me = getMe(state);
  if (!me) return [];

  const isPaidManagedEntMember = isPaidManagedEnterpriseMember({
    confirmed: me.confirmed,
    idEnterprise: me.idEnterprise,
    enterpriseLicenses: me.enterpriseLicenses,
  });

  const permissions: PermissionLevel[] = [
    'private',
    'org',
    'public',
    'enterprise',
  ];

  const orgsWithPermissions = getMyTeams(state)
    .filter(
      (org: TeamModel) =>
        !isPaidManagedEntMember ||
        org.products?.some(ProductFeatures.isEnterpriseProduct),
    )
    .map((org: TeamModel) => ({
      allowedVis: permissions.filter((vis) =>
        teamCanSetVisibility(state, org, vis),
      ),
      model: org,
    }));

  return orgsWithPermissions;
}

export const getMyTeamsByIds = (state: State, idOrganizations: string[] = []) =>
  idOrganizations.map((idOrganization) => getMyTeamById(state, idOrganization));
